import { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, IconButton, Input, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { FaSearch, FaTrash, FaUpload } from "react-icons/fa";
import { crm } from "../../../theme";
import { NveImportResult, NveReference } from "../../../models/Nve";
import {
  addNveReference,
  getNveStatus,
  importNveFile,
  removeNveReference,
  searchNveReference,
} from "../../../services/nveService";
import { formatNcm, TableCard, tdProps, thProps } from "../../process/components/tableStyles";

const labelProps = { fontSize: "12px", fontWeight: "600", color: crm.textMuted, mb: "4px" };
const inputProps = { h: "42px", borderRadius: "10px", focusBorderColor: crm.brand };

const emptyForm = { ncm: "", attributeCode: "", attributeName: "", specificationCode: "", specificationName: "" };

/** Tabela de referência NVE (admin): importação CSV/JSON e cadastro manual por NCM. */
const NveSettings = () => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [count, setCount] = useState(0);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<NveReference[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<NveImportResult>();
  const [form, setForm] = useState(emptyForm);

  const load = async (ncm?: string) => {
    const [status, list] = await Promise.all([getNveStatus(), searchNveReference(ncm)]);
    setCount(status.referenceCount);
    setRows(list);
  };

  useEffect(() => {
    load().catch(() => toast({ title: "Não foi possível carregar a tabela NVE", status: "error" }));
  }, []);

  const onFile = async (file?: File) => {
    if (!file) return;
    setImporting(true);
    try {
      const result = await importNveFile(file);
      setImportResult(result);
      toast({
        title: result.success ? "Tabela NVE importada" : "Arquivo recusado, nada foi alterado",
        description: result.success
          ? `${result.added} novas, ${result.updated} atualizadas, ${result.unchanged} iguais`
          : result.errors[0],
        status: result.success ? "success" : "error",
        duration: 8000,
      });
      await load(query);
    } catch {
      toast({ title: "Falha ao importar a tabela NVE", status: "error" });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const add = async () => {
    try {
      await addNveReference(form);
      setForm(emptyForm);
      await load(query);
    } catch (error: any) {
      const data = error?.response?.data;
      toast({
        title: "Não foi possível adicionar",
        description: data?.notifications?.[0] ?? (typeof data === "string" ? data : undefined),
        status: "error",
      });
    }
  };

  const remove = async (id: string) => {
    await removeNveReference(id);
    await load(query);
  };

  return (
    <Box>
      <Box bg="white" borderRadius="12px" border={`1px solid ${crm.border}`} boxShadow={crm.shadowSm} p="24px" mb="24px">
        <Text fontSize="16px" fontWeight="700" color={crm.text}>
          Tabela NVE
        </Text>
        <Text fontSize="13px" color={crm.textMuted} mt="2px" mb="16px">
          Atributos (2 letras) e especificações (4 dígitos) por NCM, usados no seletor de NVE dos itens do processo.
          A Receita não publica a tabela por API: importe um CSV/JSON (colunas ncm; atributo; nome do atributo;
          especificação; nome da especificação) ou cadastre abaixo. {count} registros.
        </Text>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt,.json"
          style={{ display: "none" }}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <Button
          leftIcon={<FaUpload />}
          bg={crm.brand}
          color="white"
          _hover={{ bg: crm.brandDark }}
          isLoading={importing}
          onClick={() => fileRef.current?.click()}
        >
          Importar arquivo
        </Button>
        {importResult && !importResult.success && (
          <Box mt="12px" p="12px" bg={crm.subtle} borderRadius={crm.radiusSm} border={`1px solid ${crm.border}`}>
            {importResult.errors.slice(0, 10).map((err) => (
              <Text key={err} fontSize="12px" color={crm.danger}>
                {err}
              </Text>
            ))}
          </Box>
        )}
      </Box>

      <Box bg="white" borderRadius="12px" border={`1px solid ${crm.border}`} boxShadow={crm.shadowSm} p="24px">
        <Flex gap="8px" mb="16px" align="flex-end" wrap="wrap">
          <Box>
            <Text {...labelProps}>Filtrar por NCM</Text>
            <Input {...inputProps} w="200px" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="NCM" />
          </Box>
          <Button
            h="42px"
            leftIcon={<FaSearch />}
            variant="outline"
            color={crm.brand}
            borderColor={crm.border}
            _hover={{ bg: crm.brandSoft }}
            onClick={() => load(query)}
          >
            Buscar
          </Button>
        </Flex>

        <Flex gap="8px" mb="16px" align="flex-end" wrap="wrap">
          <Box w="130px">
            <Text {...labelProps}>NCM</Text>
            <Input {...inputProps} value={form.ncm} onChange={(e) => setForm({ ...form, ncm: e.target.value })} />
          </Box>
          <Box w="80px">
            <Text {...labelProps}>Atrib.</Text>
            <Input
              {...inputProps}
              maxLength={2}
              value={form.attributeCode}
              onChange={(e) => setForm({ ...form, attributeCode: e.target.value.toUpperCase() })}
            />
          </Box>
          <Box flex="1" minW="140px">
            <Text {...labelProps}>Nome do atributo</Text>
            <Input {...inputProps} value={form.attributeName} onChange={(e) => setForm({ ...form, attributeName: e.target.value })} />
          </Box>
          <Box w="100px">
            <Text {...labelProps}>Espec.</Text>
            <Input
              {...inputProps}
              maxLength={4}
              value={form.specificationCode}
              onChange={(e) => setForm({ ...form, specificationCode: e.target.value.replace(/\D/g, "") })}
            />
          </Box>
          <Box flex="1" minW="140px">
            <Text {...labelProps}>Nome da especificação</Text>
            <Input {...inputProps} value={form.specificationName} onChange={(e) => setForm({ ...form, specificationName: e.target.value })} />
          </Box>
          <Button h="42px" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={add}>
            Adicionar
          </Button>
        </Flex>

        {rows.length === 0 ? (
          <Text color={crm.textFaint}>Nenhum registro.</Text>
        ) : (
          <TableCard>
            <Thead>
              <Tr>
                <Th {...thProps}>NCM</Th>
                <Th {...thProps}>Atributo</Th>
                <Th {...thProps}>Especificação</Th>
                <Th {...thProps} />
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => (
                <Tr key={r.id} _hover={{ bg: crm.brandSoft }}>
                  <Td {...tdProps} fontWeight="600">{formatNcm(r.ncm)}</Td>
                  <Td {...tdProps}>{r.attributeCode} · {r.attributeName ?? ""}</Td>
                  <Td {...tdProps}>{r.specificationCode} · {r.specificationName ?? ""}</Td>
                  <Td {...tdProps} isNumeric>
                    <IconButton
                      aria-label="remover"
                      size="sm"
                      variant="ghost"
                      icon={<FaTrash />}
                      color={crm.textMuted}
                      _hover={{ color: crm.danger }}
                      onClick={() => remove(r.id)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </TableCard>
        )}
        {rows.length >= 500 && (
          <Text fontSize="12px" color={crm.textFaint} mt="8px">
            Mostrando os primeiros 500: filtre por NCM para ver mais.
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default NveSettings;
